<template>
  <MuseumLayout>
    <template #challenge-tab>
      <ChallengeCard>
        <template #top>
          <InstructionHeader
            >Hokusai Pigments Part 1: Spectra</InstructionHeader
          >
          <InstructionRow rowType="Tool">
            Compare the spectra of the regions of interest (ROI) with comparison
            pigments to see if you can identify the ROI pigments.
            <template #before-steps>
              <div class="mb-2">
                (Find the Spectrum Tool by scrolling down below the painting.)
              </div>
            </template>
            <template #after-steps>
              <div class="mb-2">
                NOTE: Many of the reference pigment spectra for different colors
                display absorption features at a common set of wavelengths:
                1.39–1.67, 1.94, 2.11, 2.28, and 2.34 microns. These are
                contributed by cellulose in the paper that the pigments were
                applied to. The Hokusai painting itself is done on silk.
                Reference spectra for cellulose and silk are included, so you
                can account for their features in the spectra.
              </div>
            </template>
            <template #steps>
              <InstructionStep>
                Under <span class="challenge-bold">SOURCE 1</span>, use the
                <span class="challenge-bold">Select Hokusai ROI</span>
                dropdown menu to view a spectrum for each region of interest.
              </InstructionStep>
              <InstructionStep>
                Under <span class="challenge-bold">SOURCE 2</span>, use the
                <span class="challenge-bold">Select material</span> dropdown
                menu to view a spectrum for different pigments of each color or
                the cellulose or silk backgrounds.
              </InstructionStep>
              <InstructionStep>
                Adjust the
                <span class="challenge-tool-label">WAVELENGTH STRETCH</span> as
                needed to view the complete spectrum.
              </InstructionStep>
            </template>
          </InstructionRow>
          <InstructionRow rowType="Notebook">
            <template #steps>
              <InstructionStep>
                For each ROI, write the pigment names you identified in your
                notebook table.
              </InstructionStep>
              <InstructionStep>
                Some pigments may be easier to identify from their spectra than
                others. Use the last column to note how much confidence you have
                in your identification of the pigments for each ROI.
              </InstructionStep>
            </template>
          </InstructionRow>
        </template>
        <template #middle>
          <ImageZoomOverlay :src="hokusaiChallenge" :zoom-src="hokusaiBig" />
        </template>
      </ChallengeCard>
    </template>
    <template #tool-col>
      <ToolControlGroup
        :control-names="['units', 'plotType', 'normalize', 'zoom']"
      >
        <template #top-tool>
          <ToolCard
            :custom-metadata="hokusaiROIsMetadata"
            spectrum-picker-placeholder="Select Hokusai ROI"
          />
        </template>
        <template #bottom-tool>
          <ToolCard
            :custom-metadata="pigmentMetadata"
            spectrum-picker-placeholder="Select material"
          />
        </template>
      </ToolControlGroup>
      <LeftRightGroup class="mt-5">
        <template #left>
          <NextPrevButton to="../hokusai-painting" direction="prev" light>
            previous section
          </NextPrevButton>
        </template>
        <template #right>
          <NextPrevButton to="data-table" direction="next" light>
            part 2
          </NextPrevButton>
        </template>
      </LeftRightGroup>
    </template>
  </MuseumLayout>
</template>

<script setup lang="ts">
import hokusaiChallenge from '@/assets/spectrum_data/Museum_Conservation/hokusai_visible_b1b2w1y1_910.webp';
import hokusaiBig from '@/assets/spectrum_data/Museum_Conservation/hokusai_visible_b1b2w1y1_big.webp';
import { useSpecLabHead } from '@/utils/locationUtils';
import { useCustomMetadata, useAllMetadata } from '@/utils/metadataUtils';

useSpecLabHead('Hokusai Pigments Part 1: Spectra', 'Museum');

const hokusaiB1 = useCustomMetadata(
  'Painting Regions',
  'F1904-134_VNIR-SWIR_colors_Blue_1',
  {
    title: 'ROI B1',
    imageUrl: '',
    bigImageUrl: '',
  },
);

const hokusaiB2 = useCustomMetadata(
  'Painting Regions',
  'F1904-134_VNIR-SWIR_colors_Blue_2',
  {
    title: 'ROI B2',
    imageUrl: '',
    bigImageUrl: '',
  },
);

const hokusaiW1 = useCustomMetadata(
  'Painting Regions',
  'F1904-134_VNIR-SWIR_colors_White_1',
  {
    title: 'ROI W1',
    imageUrl: '',
    bigImageUrl: '',
  },
);

const hokusaiY = useCustomMetadata(
  'Painting Regions',
  'F1904-134_VNIR-SWIR_colors_Yellow',
  {
    title: 'ROI Y1',
    imageUrl: '',
    bigImageUrl: '',
  },
);

const customMetadataMaybe = [hokusaiB1, hokusaiB2, hokusaiW1, hokusaiY];
const customMetadata = customMetadataMaybe.filter((sm) => !!sm);

const hokusaiROIsMetadata = customMetadata.filter(
  (sm) =>
    sm.filename === 'F1904-134_VNIR-SWIR_colors_Blue_1' ||
    sm.filename === 'F1904-134_VNIR-SWIR_colors_Blue_2' ||
    sm.filename === 'F1904-134_VNIR-SWIR_colors_White_1' ||
    sm.filename === 'F1904-134_VNIR-SWIR_colors_Yellow',
);

const allMetadata = useAllMetadata();
const pigmentMetadata = allMetadata['Paint Pigments & Supplies'];
</script>
